import * as React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import ChecklistIcon from '@mui/icons-material/Checklist';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { IPickupTask } from '../../../../interfaces/delivery-company/tasks';
import { isMobile, isTablet } from 'react-device-detect';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  TextField,
} from '@mui/material';
import SnackBar from '../../../common/snack-bar/snack-bar';
import { timestampedMessage } from '../../../common/multi-package-scanner/multi-package-scanner';
import { PickupTaskState } from '../../../../enums/tasks';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import axios, { AxiosResponse } from 'axios';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { configs } from 'eslint-plugin-prettier';
import SendMessageDialog from '../../common/send-message-dialog/send-message-dialog';
import {
  NotificationTemplate,
  OrganisationSettings,
} from '../../../../interfaces/delivery-company/orders';
import GavelIcon from '@mui/icons-material/Gavel';
import { useImmer } from 'use-immer';
import { red } from '@mui/material/colors';
import WarningIcon from '@mui/icons-material/Warning'; // Import the warning icon
import { Workflow } from '../../../../enums/orders';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
  displayLocation,
  extractMapsPlaceLink,
} from '../../../../utils/locations';
import StaticGoogleMap from '../../../common/static-google-map/static-google-map';
import { GenericList } from '../../../common/generic-list/generic-list';
import { ALL, FINISHED, IN_PROGRESS, PENDING } from '../filter_utils';
import MarkAsFailedDialog from '../../common/mark-as-failed-dialog';
import { pickup_failure_reasons } from '../constants';
import AddNotesDialog from '../../common/add-notes-dialog';
import PickupTaskQuickScanDialog from '../../quick-scan-dialogs/pickup-task-dialog';
import { buildResolveTemplateArgsForPickup } from '../../../delivery-company-components/settings/notification-manager/utils';
import { useSnackBar } from '../../../../contexts/snackbar';

interface DynamicCardListingProps {
  isLoading: boolean;
  tasks: IPickupTask[];
  statusFilter: string;
  reloadList: () => void;
  totalCount: number;
  loadMoreTasks: () => void;
  updateTasks: (mutator: (task: IPickupTask) => IPickupTask) => void;
}
export default function DynamicCardListing({
  isLoading,
  tasks,
  statusFilter,
  reloadList,
  totalCount,
  loadMoreTasks,
  updateTasks,
}: DynamicCardListingProps) {
  const [selectedTasks, updateSelectedTasks] = useImmer<
    Map<string, IPickupTask>
  >(new Map());

  const [whatsAppDialogOpen, setWhatsAppDialogOpen] = React.useState(false);
  const totalLoadedTasks = tasks.length;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notificationTemplates, setNotificationTemplates] =
    React.useState<NotificationTemplate[]>();
  const [markAsFailedDialogOpen, setMarkAsFailedDialogOpen] =
    React.useState(false);
  const [addNoteDialogOpen, setAddNoteDialogOpen] = React.useState(false);

  const [viewingPickupTask, setViewingPickupTask] =
    React.useState<IPickupTask | null>(null);

  const [snackBar, setSnackBar] = React.useState<timestampedMessage>();
  const { user, config } = React.useContext(AuthContext);
  const [response, setResponse] = React.useState<AxiosResponse>();

  const { showMessage } = useSnackBar();

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  React.useEffect(() => {
    updateSelectedTasks((prev) => prev.clear());
  }, [statusFilter]);

  const handleWhatsAppMenuItemClick = (action: string) => {
    setWhatsAppDialogOpen(true);
    handleMenuClose();
  };
  const handleSendWhatAppMessage = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/pickup-tasks/notify/`;
    const requestData = {
      message: template,
      task_ids: Array.from(selectedTasks.values()).map((task) => task.id),
    };

    return axios.post(endpoint, requestData, config);
  };

  const handleStartSingleTask = async (id: string) => {
    try {
      const endpoint = `${API_ENDPOINT}/api/v1/pickup-task/${id}/`;
      const requestData = { status: PickupTaskState.IN_PROGRESS };
      await axios.patch(endpoint, requestData, config);
      updateTasks((t) =>
        t.id == id ? { ...t, status: PickupTaskState.IN_PROGRESS } : t
      );
      return true;
    } catch (error) {
      return false;
    }
  };
  const handleStartTaskMenuClick = async () => {
    try {
      const endpoint = `${API_ENDPOINT}/api/v1/pickup-tasks/bulk-update/`;
      const requestData = Array.from(selectedTasks.values()).map((t) => {
        return {
          id: t.id,
          status: PickupTaskState.IN_PROGRESS,
        };
      });

      await axios.patch(endpoint, requestData, config);
      showMessage('Tasks Started');
      reloadList();
    } catch (error) {
      showMessage('Failed to start tasks', 'error');
    }
    handleMenuClose();
    updateSelectedTasks((prev) => prev.clear());
  };

  const handleMarkAsFailedTasks = async (failure_reason: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/pickup-tasks/bulk-update/`;
    const requestData = Array.from(selectedTasks.values()).map((t) => {
      return {
        id: t.id,
        failure_reason: failure_reason,
        status: PickupTaskState.FAILED,
      };
    });
    return axios.patch(endpoint, requestData, config);
  };

  React.useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const endpoint = `${API_ENDPOINT}/api/v1/settings`;
        const response = await axios.get<OrganisationSettings>(
          endpoint,
          config
        );
        setNotificationTemplates(response.data.notification_templates);
        console.log(response.data);
      } catch (e) {
        setSnackBar({
          timestamp: Date.now(),
          content: 'Failed to load notification templates',
          level: 'error',
        });
      }
    };

    fetchTemplates();
  }, []);

  const handleAddNote = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/pickup-tasks/bulk-update/`;
    const requestData = Array.from(selectedTasks.values()).map((t) => {
      return {
        id: t.id,
        notes: [template],
      };
    });
    return axios.patch(endpoint, requestData, config);
  };

  const actionsAllowed = statusFilter !== FINISHED && statusFilter !== ALL;

  return (
    <Box sx={{ padding: 0 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 10,
          marginLeft: 10,
        }}
      >
        <Chip
          icon={<ChecklistIcon />}
          color="primary"
          label={`Selected ${selectedTasks.size}/${totalCount}`}
          variant={
            selectedTasks.size === totalLoadedTasks && selectedTasks.size > 0
              ? 'filled'
              : 'outlined'
          }
        />
        {actionsAllowed && (
          <div>
            <Button
              size={(isMobile && !isTablet) ? 'small' : 'medium'}
              variant="outlined"
              aria-controls="actions-menu"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              disabled={selectedTasks.size === 0}
            >
              Actions
            </Button>
            <Menu
              id="actions-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={() => handleWhatsAppMenuItemClick('Send Message')}
              >
                Send Message{' '}
                <WhatsAppIcon fontSize="small" style={{ marginLeft: '8px' }} />
              </MenuItem>
              {statusFilter == PENDING && (
                <MenuItem onClick={() => handleStartTaskMenuClick()}>
                  Start Task{' '}
                  <GavelIcon fontSize="small" style={{ marginLeft: '8px' }} />
                </MenuItem>
              )}
              {(statusFilter == PENDING || statusFilter == IN_PROGRESS) && (
                <MenuItem onClick={() => setMarkAsFailedDialogOpen(true)}>
                  Mark as Failed
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  setAddNoteDialogOpen(true);
                  handleMenuClose();
                }}
              >
                Add note
              </MenuItem>
            </Menu>
          </div>
        )}
      </div>

      <GenericList<IPickupTask>
        sourceView="driver-pickups-listing"
        items={tasks}
        rowConfigs={[
          {
            display_row: (task: IPickupTask) => <b>Pickup #{task.id}</b>,
          },
          {
            display_row: (task) => (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: '#f0f0f0', // Light gray background
                  borderRadius: '12px', // Rounded corners
                  padding: '4px 8px', // Padding around the text
                }}
              >
                {task.package_count} items
              </Box>
            ),
          },
          {
            display_row: (task) => (
              <div
                style={{
                  display: 'flex',
                  alignContent: 'center',
                  justifyItems: 'center',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {task.workflow === Workflow.RETURN ? (
                  <StorefrontIcon fontSize="small" />
                ) : (
                  <PersonOutlineIcon fontSize="small" />
                )}{' '}
                {task.workflow === Workflow.STANDARD
                  ? task.merchant?.name
                  : task.customer?.name}
              </div>
            ),
          },
          {
            display_row: (task) => (
              <div
                style={{
                  display: 'flex',
                  alignContent: 'center',
                  justifyItems: 'center',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <LocationOnIcon fontSize="small" />{' '}
                {task.workflow === Workflow.RETURN
                  ? displayLocation(task.customer_location)
                  : displayLocation(task.merchant_location)}
              </div>
            ),
          },
          {
            display_row: (task) => (
              <div style={{ color: red[400] }}>
                {(task.notes ?? []).join(' | ')}
              </div>
            ),
          },
          {
            display_row: (task) => (
              <div style={{ color: red[400] }}>{task.failure_reason}</div>
            ),
          },
          {
            display_row: (task) => {
              const mapPath = extractMapsPlaceLink(
                task.workflow == Workflow.RETURN
                  ? task.customer_location
                  : task.merchant_location
              );
              const url =
                task.workflow == Workflow.RETURN
                  ? task.customer_location?.url
                  : task.merchant_location?.url;
              return <StaticGoogleMap mapPath={mapPath} url={url} />;
            },
          },
        ]}
        isLoading={isLoading}
        selectedItems={selectedTasks}
        updateSelectedItems={updateSelectedTasks}
        getItemUID={(task) => task.id}
        cardIcon={(task) => {
          return task.is_critical ? (
            <WarningIcon
              sx={{
                color: red[500],
              }}
            />
          ) : undefined;
        }}
        iconBoxStyles={(task) => {
          return {
            padding: '4px',
            boxShadow: `0 0 8px ${red[500]}`,
          };
        }}
        cardFooter={(task) => {
          return {
            content: <b>{task.status.replaceAll('_', ' ').toUpperCase()}</b>,
            background: 'lightgrey',
          };
        }}
        onCardClick={(item) => {
          setViewingPickupTask(item);
        }}
        columns={[]}
        selectionEnabled={actionsAllowed}
      />

      {whatsAppDialogOpen && notificationTemplates && (
        <SendMessageDialog
          stage={'Pickup'}
          setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
          onSubmit={handleSendWhatAppMessage}
          onClose={() => setWhatsAppDialogOpen(false)}
          availableTemplates={notificationTemplates}
          onSuccess={() => {
            /**to be implemented */
          }}
          locallyResolvedTargets={Array.from(selectedTasks.values()).map(
            (task) => {
              return {
                id: task.id,
                label: `Pickup #${task.id}`,
                phone_number:
                  task.workflow === Workflow.RETURN
                    ? task.customer?.phone_number ?? ''
                    : task.merchant_location?.contact_phone ?? '',
                resolveTemplateArgs: buildResolveTemplateArgsForPickup(
                  task,
                  user?.name ?? '',
                  user?.organisation?.org_id ?? 'ERROR'
                ),
                alreadyStarted: task.status != PickupTaskState.ASSIGNED,
              };
            }
          )}
          startTask={handleStartSingleTask}
        />
      )}
      {viewingPickupTask && (
        <PickupTaskQuickScanDialog
          task={viewingPickupTask}
          onClose={() => setViewingPickupTask(null)}
        />
      )}
      {markAsFailedDialogOpen && (
        <MarkAsFailedDialog
          setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
          onSubmit={handleMarkAsFailedTasks}
          onClose={() => setMarkAsFailedDialogOpen(false)}
          failure_reasons={pickup_failure_reasons}
          onSuccess={(r) => {
            reloadList();
            updateSelectedTasks((prev) => prev.clear());
          }}
        />
      )}
      {addNoteDialogOpen && (
        <AddNotesDialog
          setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
          onSubmit={handleAddNote}
          onSuccess={(m: string) =>
            updateTasks((p) =>
              selectedTasks.has(p.id)
                ? { ...p, notes: [...(p.notes ?? []), m] }
                : p
            )
          }
          onClose={() => setAddNoteDialogOpen(false)}
        />
      )}
      {totalLoadedTasks < totalCount && (
        <Button
          fullWidth
          variant="outlined"
          onClick={() => loadMoreTasks()}
          sx={{ marginTop: 2, marginBottom: 2.5 }}
        >
          See More
        </Button>
      )}

      {snackBar && snackBar.content && snackBar.content !== '' && (
        <SnackBar
          message={snackBar.content}
          level={snackBar.level}
          key={snackBar.timestamp}
        />
      )}
    </Box>
  );
}
